import React from 'react';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, BarChart3, Minus, PhoneCall, RefreshCw } from 'lucide-react';

import { formatIST } from '@/lib/utils';
import {
  Card, EmptyState, IconButton, PageHeader, PhonePage, PillButton, ScreenLoader, StatusPill, TabRow,
} from '@/components/phone/primitives';
import { Colors, statusTone } from '@/components/phone/theme';
import { inr, plural } from './format';

/**
 * The owner's four questions, on the phone the owner reads them on.
 *
 * This is the screen opened over breakfast, before anyone is at a desk, so it
 * runs top to bottom in the order the questions are asked: the four headlines
 * two to a row, the book as one card of facts, then the list of people to ring
 * with the late and due-soon orders one tab away, and the floor last. Every
 * sentence comes from the page as data, so the phone never words a number
 * differently from the laptop.
 */

const TREND_ICON = { up: ArrowUpRight, down: ArrowDownRight, flat: Minus };

const TONE_COLOR = {
  positive: Colors.semantic.positive,
  caution: Colors.semantic.caution,
  critical: Colors.semantic.critical,
  muted: Colors.text.disabled,
  neutral: Colors.text.secondary,
};

const SEVERITY_TONE = { critical: 'critical', high: 'critical', medium: 'caution', low: 'neutral' };

function Headline({ label, value, valueTone, note, noteTone, trendDir, onClick, testID }) {
  const Icon = trendDir && TREND_ICON[trendDir];
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testID}
      className="flex min-w-0 flex-col gap-1 rounded-[14px] border p-4 text-left"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <span className="truncate text-[11px] font-semibold uppercase tracking-[0.6px]" style={{ color: Colors.text.secondary }}>
        {label}
      </span>
      <span className="truncate text-[26px] font-bold leading-tight tabular-nums -tracking-[0.5px]"
        style={{ color: TONE_COLOR[valueTone] || Colors.text.primary }}>
        {value}
      </span>
      <span className="flex items-start gap-1 text-[11px] leading-4" style={{ color: TONE_COLOR[noteTone] || Colors.text.secondary }}>
        {Icon && <Icon className="mt-0.5 h-3 w-3 shrink-0" />}
        <span className="line-clamp-2">{note}</span>
      </span>
    </Tag>
  );
}

function FactRow({ label, value, valueTone, sub, subTone }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b py-2.5 last:border-b-0 last:pb-0 first:pt-0"
      style={{ borderColor: Colors.border.subtle }}>
      <span className="text-[13px] font-medium" style={{ color: Colors.text.secondary }}>{label}</span>
      <span className="flex min-w-0 flex-col items-end text-right">
        <span className="text-[14px] font-semibold tabular-nums" style={{ color: TONE_COLOR[valueTone] || Colors.text.primary }}>
          {value ?? '—'}
        </span>
        {sub != null && (
          <span className="text-[11px]" style={{ color: TONE_COLOR[subTone] || Colors.text.tertiary }}>{sub}</span>
        )}
      </span>
    </div>
  );
}

function ListCard({ children, onClick, testID }) {
  return (
    <li className="mt-2">
      <button
        type="button"
        data-testid={testID}
        onClick={onClick}
        className="block w-full rounded-[10px] border px-4 py-3 text-left"
        style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
      >
        {children}
      </button>
    </li>
  );
}

/** One customer to ring, with every reason to, worded the way it would be said. */
function CallCard({ customer: c, onOpen }) {
  return (
    <ListCard onClick={onOpen} testID={`call-card-${c.customer_id}`}>
      <span className="block truncate text-[15px] font-bold" style={{ color: Colors.text.primary }}>
        {c.firm_name || c.customer_name}
      </span>
      {c.firm_name && c.customer_name !== c.firm_name && (
        <span className="block truncate text-[12px]" style={{ color: Colors.text.secondary }}>{c.customer_name}</span>
      )}
      <span className="mt-2 flex flex-wrap gap-1.5">
        {c.late_orders > 0 && (
          <StatusPill tone="critical" label={`${plural(c.late_orders, 'order')} late by up to ${plural(c.worst_days_late, 'day')}`} />
        )}
        {c.open_complaints > 0 && (
          <StatusPill tone={SEVERITY_TONE[c.worst_severity] || 'caution'} label={plural(c.open_complaints, 'open complaint')} />
        )}
        {c.owed_stale > 0 && (
          <StatusPill tone="caution" label={`${inr(c.owed_stale)} unpaid ${c.owed_days} days`} />
        )}
        {c.quiet_days > 0 && <StatusPill tone="neutral" label={`no order in ${c.quiet_days} days`} />}
      </span>
    </ListCard>
  );
}

/** A late or due-soon order: who, what, how far off the date, and what it is worth. */
function RiskCard({ order: o, late, onOpen }) {
  const days = late ? o.days_late : -o.days_late;
  const when = days <= 0 ? 'today' : plural(days, 'day');
  const tone = late ? (days >= 7 ? 'critical' : 'caution') : 'neutral';
  return (
    <ListCard onClick={onOpen} testID={`risk-card-${o.id}`}>
      <span className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="block truncate text-[15px] font-bold" style={{ color: Colors.text.primary }}>
            {o.firm_name || o.customer_name}
          </span>
          <span className="block truncate text-[12px]" style={{ color: Colors.text.secondary }}>
            {o.item_name || o.order_id}
          </span>
        </span>
        <span className="shrink-0 text-right text-[13px] font-bold" style={{ color: TONE_COLOR[tone] || Colors.text.primary }}>
          {late ? `${when} late` : `due ${days <= 0 ? 'today' : `in ${when}`}`}
        </span>
      </span>
      <span className="mt-2 flex flex-wrap items-center gap-2 text-[11px]" style={{ color: Colors.text.tertiary }}>
        <StatusPill label={(o.status || '').replace(/_/g, ' ')} tone={statusTone(o.status)} dot />
        <span>{o.expected_delivery_date ? `promised ${formatIST(o.expected_delivery_date, { dateOnly: true })}` : 'no date'}</span>
        {!!o.order_value && (
          <span className="ml-auto text-[13px] font-semibold tabular-nums" style={{ color: Colors.text.primary }}>
            {inr(o.order_value)}
          </span>
        )}
      </span>
    </ListCard>
  );
}

function StageRow({ stage: s, share }) {
  const sub = [
    s.avg_days == null ? null : `${s.avg_days}d to clear`,
    s.reject_rate ? `${s.reject_rate}% spoiled` : null,
  ].filter(Boolean).join(' · ');
  return (
    <div className="relative flex items-center gap-2 overflow-hidden rounded-md px-2.5 py-2">
      <span
        className="absolute inset-y-0 left-0"
        style={{ width: `${Math.max(share, 2)}%`, backgroundColor: s.reject_rate >= 5 ? Colors.semantic.criticalWash : Colors.accent.wash }}
        aria-hidden
      />
      <span className="relative min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium" style={{ color: Colors.text.primary }}>{s.stage}</span>
        {!!sub && <span className="block truncate text-[11px]" style={{ color: Colors.text.secondary }}>{sub}</span>}
      </span>
      <span className="relative shrink-0 text-[14px] font-bold tabular-nums" style={{ color: Colors.text.primary }}>{s.waiting}</span>
    </div>
  );
}

export default function PhoneDashboard({
  title, today, loading, failed, onRefresh, onReports,
  headlines, facts, tab, onTab,
  attention, late, dueSoon, complaints, stages, busiest,
  onCustomer, onOrder,
}) {
  const tabs = [
    { key: 'calls', label: `Calls ${attention.length}`, testID: 'board-tab-calls' },
    { key: 'late', label: `Late ${late.length}`, testID: 'board-tab-late' },
    { key: 'soon', label: `Due soon ${dueSoon.length}`, testID: 'board-tab-soon' },
  ];
  const risk = tab === 'late' ? late : dueSoon;

  return (
    <PhonePage testID="dashboard-phone">
      <PageHeader
        title={title}
        subtitle={today}
        action={(
          <span className="flex shrink-0 items-center gap-2">
            <IconButton icon={RefreshCw} label="Refresh" onClick={onRefresh} testID="refresh-btn" />
            <PillButton onClick={onReports} testID="reports-btn">
              <BarChart3 className="h-4 w-4" /> Reports
            </PillButton>
          </span>
        )}
      />

      {loading ? (
        <ScreenLoader />
      ) : failed ? (
        <div className="px-5 py-16 text-center">
          <p className="text-[15px] font-medium" style={{ color: Colors.status.error }}>The dashboard did not answer</p>
          <p className="mt-1 text-[13px]" style={{ color: Colors.text.tertiary }}>Nothing is wrong with your data — try again.</p>
          <button
            type="button"
            onClick={onRefresh}
            className="mt-4 rounded-[10px] border px-5 py-2 text-[13px] font-bold"
            style={{ borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4 px-5 pb-8">
          <div className="grid grid-cols-2 gap-3" data-testid="metrics-grid">
            {headlines.map(({ key, ...h }) => <Headline key={key} testID={`headline-${key}`} {...h} />)}
          </div>

          <Card title="The book" aside="this month" testID="facts-card">
            {facts.map(({ key, ...f }) => <FactRow key={key} {...f} />)}
          </Card>

          <div>
            <TabRow tabs={tabs} active={tab} onSelect={onTab} />
            {tab === 'calls' ? (
              attention.length === 0 ? (
                <EmptyState icon={PhoneCall} message="Nobody to chase"
                  hint="No missed dates, no open complaints, no stale bills." />
              ) : (
                <ul data-testid="call-list">
                  {attention.map((c) => <CallCard key={c.customer_id} customer={c} onOpen={() => onCustomer(c)} />)}
                </ul>
              )
            ) : risk.length === 0 ? (
              <EmptyState icon={AlertTriangle}
                message={tab === 'late' ? 'Nothing is late' : 'Nothing due in three days'}
                hint={tab === 'late'
                  ? 'Every open order is inside its promised date.'
                  : 'Nothing falls due in the next three days.'} />
            ) : (
              <ul data-testid="at-risk-list">
                {risk.map((o) => <RiskCard key={o.id} order={o} late={tab === 'late'} onOpen={() => onOrder(o.id)} />)}
              </ul>
            )}
          </div>

          <Card title="Open complaints" aside={complaints.length ? `${complaints.length} unresolved` : undefined} testID="complaints-card">
            {complaints.length === 0 ? (
              <p className="py-2 text-center text-[12px]" style={{ color: Colors.text.tertiary }}>
                Nothing outstanding — every issue raised has been closed out.
              </p>
            ) : complaints.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onOrder(c.order_uuid)}
                className="flex w-full items-start gap-3 border-b py-2.5 text-left last:border-b-0 last:pb-0"
                style={{ borderColor: Colors.border.subtle }}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold" style={{ color: Colors.text.primary }}>
                    {c.firm_name || c.customer_name}
                  </span>
                  <span className="line-clamp-2 block text-[12px]" style={{ color: Colors.text.secondary }}>
                    {c.description || c.item_name || c.order_id}
                  </span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <StatusPill label={c.severity} tone={SEVERITY_TONE[c.severity] || 'caution'} />
                  <span className="text-[11px]" style={{ color: Colors.text.tertiary }}>{plural(c.days_open, 'day')}</span>
                </span>
              </button>
            ))}
          </Card>

          <Card title="Where the work is" aside="queued now" testID="stages-card">
            {stages.length === 0 ? (
              <p className="py-2 text-center text-[12px]" style={{ color: Colors.text.tertiary }}>Nothing on the floor right now.</p>
            ) : (
              <div className="-mx-1 flex flex-col gap-1">
                {stages.map((s) => <StageRow key={s.stage} stage={s} share={(s.waiting / busiest) * 100} />)}
              </div>
            )}
          </Card>
        </div>
      )}
    </PhonePage>
  );
}
